import { QueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/api/errors';

/**
 * The shared query client.
 *
 * Defaults are tuned for an internal CRM rather than a public site — the
 * differences below are deliberate.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,

      // Retrying a 4xx is pointless: a 403 will still be a 403, and a 422 will
      // still be invalid. Retrying them just delays the error the user needs to
      // see. Only server faults and transport failures are worth a second try.
      retry: (failureCount, error) => {
        const status = (error as unknown as ApiError).status;
        if (status >= 400 && status < 500) return false;
        return failureCount < 2;
      },

      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),

      // Refetch-on-focus is wrong here. A salesperson alt-tabs to their inbox
      // mid-form and comes back; a refetch would swap the data under a form they
      // are halfway through filling in.
      refetchOnWindowFocus: false,

      refetchOnReconnect: true,
    },

    mutations: {
      // Never automatically retried. A create that appears to fail but actually
      // succeeded would produce a duplicate contact on retry — and duplicates are
      // exactly what this application exists to prevent.
      retry: false,
    },
  },
});
